import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
  Cast,
  Airplay,
  X,
  Tv,
  ExternalLink,
  Info,
  Play,
  Pause,
  Square,
  Volume2,
  VolumeX,
  Volume1,
  CheckCircle2,
  AlertCircle,
  Loader2,
  SkipBack,
  SkipForward,
} from 'lucide-react';
import { extractChannelQualityInfo, RESOLUTION_HIERARCHY } from '../../utils/channelQualityUtils';
import { extractStreamIdFromChannel } from '../../utils/epgService';
import { useIPTV } from '../../context/IPTVContext';
import { getProxiedStreamUrl, convertToHlsUrl } from '../../utils/streamProxy';
import { withServerHost, withListAccount } from '../../utils/managerDns';
import { apiClient } from '../../lib/apiClient';
import type { Channel, Episode, ChannelQualityVariant } from '../../types/iptv';

/**
 * Transmitir para a TV (V8.8.4).
 * Só 2 jeitos: o NATIVO do aparelho (AirPlay no iPhone/iPad; espelhamento de tela no Android) e o
 * OPEN TV CONNECTION por código (página /tv aberta no navegador da TV).
 *
 * Por código: todos os comandos vão pelo tv_sync.php (a página /tv só escuta por lá — o WebSocket antigo
 * não chegava nela). O "tocar" leva a lista do que dá para trocar na TV:
 *  - ao vivo: os canais da categoria atual (trocar de canal na TV ou pelo celular);
 *  - série: os episódios (próximo / anterior, card de próximo episódio no fim);
 *  - filme: só ele.
 * Cada item já vai com o endereço pronto (servidor ativo do Gerenciador) e um endereço reserva.
 */

interface CastModalProps {
  isOpen: boolean;
  onClose: () => void;
  videoElement?: HTMLVideoElement | null;
  streamTitle?: string;
  directStreamUrl?: string;
  proxiedStreamUrl?: string;
  channelName?: string;
  streamUrl?: string;
  streamType?: 'live' | 'movie' | 'series';
}

interface PairedTvData {
  sessionId: string;
  tvCode: string;
  tvName: string;
  pairedAt?: number;
  lastUsed?: number;
}

/** Item que a TV sabe tocar sozinha (canal, filme ou episódio). */
interface TvItem {
  id: string;
  title: string;
  /** filme/episódio: endereço pronto */
  url?: string;
  fallback?: string;
  /** ao vivo: endereço no servidor ativo (a TV monta o do proxy com o modelo `proxy`) */
  u?: string;
  /** ao vivo: canal prefere .ts */
  ts?: 1;
  /** ao vivo: id do canal no servidor (programação pelo epg.php) */
  sid?: string;
  /** ao vivo: qualidades (SD/HD/FHD/4K) e a atual */
  q?: { r: string; u: string; ts?: 1 }[];
  cq?: string;
  num?: number;
  logo?: string;
  s?: number;
  e?: number;
  epTitle?: string;
  /** episódio já assistido */
  w?: 1;
  /** episódio: % já assistido */
  p?: number;
}

/** Máximo de canais mandados para a TV (categoria "Todos" pode ter milhares). */
const MAX_LIVE_ITEMS = 300;

const parseNum = (val: any, fallback: number) => {
  if (val === undefined || val === null) return fallback;
  const parsed = parseInt(String(val), 10);
  return isNaN(parsed) ? fallback : parsed;
};

/**
 * Último ponto informado pela TV (filme/episódio). O VodPlayer usa ao parar o Cast para voltar onde a TV
 * parou, em vez de gravar o ponto antigo do celular por cima do "Continuar Assistindo".
 */
let lastTvPosition: { contentId: string; time: number } | null = null;

/**
 * V8.8.7: último pacote mandado para a TV (lista de canais/episódios). O controle remoto usa para mostrar
 * a lista e trocar de canal/episódio. Guardado na sessão do navegador (sobrevive a recarregar a página).
 */
const CAST_PAYLOAD_KEY = 'opentv_cast_payload';
let lastCastPayload: { sessionId: string; payload: Record<string, any> } | null = null;
export function getCastPayload(sessionId?: string | null): Record<string, any> | null {
  if (!lastCastPayload) {
    try {
      const raw = sessionStorage.getItem(CAST_PAYLOAD_KEY);
      if (raw) lastCastPayload = JSON.parse(raw);
    } catch {}
  }
  if (!lastCastPayload || (sessionId && lastCastPayload.sessionId !== sessionId)) return null;
  return lastCastPayload.payload;
}
function saveCastPayload(sessionId: string, payload: Record<string, any>) {
  lastCastPayload = { sessionId, payload };
  try {
    sessionStorage.setItem(CAST_PAYLOAD_KEY, JSON.stringify(lastCastPayload));
  } catch {}
  try {
    window.dispatchEvent(new CustomEvent('opentv:cast-payload'));
  } catch {}
}
/** Abre o controle remoto do Cast (barra "Transmitindo"). */
export function openCastRemote() {
  try {
    window.dispatchEvent(new CustomEvent('opentv:cast-remote-open'));
  } catch {}
}
export function getLastTvPosition() {
  return lastTvPosition;
}

const isOracleUrl = (u?: string) => !!u && (u.includes('stream.mariozeli.com.br') || u.includes('/api/proxy'));

export interface CastSenderOptions {
  streamTitle?: string;
  directStreamUrl?: string;
  proxiedStreamUrl?: string;
  channelName?: string;
  streamUrl?: string;
  streamType?: 'live' | 'movie' | 'series';
}

/**
 * Monta o pacote do "tocar na TV" a partir do que o app está tocando (canal + canais da categoria,
 * episódio + episódios da série, ou filme) e envia comandos pelo tv_sync.php.
 * Usado pela janela do Cast e pela pergunta "Seu perfil está reproduzindo em outra tela" (Enviar para a TV).
 */
export function useCastSender({
  streamTitle,
  directStreamUrl,
  proxiedStreamUrl,
  channelName,
  streamUrl,
  streamType = 'live',
}: CastSenderOptions = {}) {
  const {
    currentProfile,
    currentProfileId,
    deviceId,
    currentChannel,
    currentMovie,
    currentSeries,
    currentEpisode,
    channels,
    series,
    activeChannelCategory,
    categoryConfig,
    isFavorite,
    getStreamServer,
    getStreamAccount,
    getChannelStreamProfile,
    isContentWatched,
    getContentProgress,
    loadSeriesEpisodes,
  } = useIPTV();

  // Identificação do dono do Cast: o servidor usa o login (token) + perfil para mostrar a TV no Gerenciador
  // no usuário certo, conferir o limite de telas e salvar o progresso no "Continuar Assistindo".
  const tvSyncHeaders = useCallback((): Record<string, string> => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const token = apiClient.getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    return headers;
  }, []);

  const ownerFields = useMemo(
    () => ({
      owner_profile_id: currentProfileId || '',
      owner_profile_name: currentProfile?.name || '',
      sender_device_id: deviceId || '',
    }),
    [currentProfileId, currentProfile, deviceId]
  );

  // Conteúdo em reprodução (para o histórico): episódio leva também o id da série
  const contentFields = useMemo(() => {
    if (currentMovie) return { contentId: currentMovie.id };
    if (currentSeries && currentEpisode)
      return {
        contentId: currentEpisode.id,
        seriesId: currentSeries.id,
        // temporada/episódio vão junto para o "Continuar Assistindo" (o servidor grava no histórico)
        seasonNumber: currentEpisode.seasonNumber,
        episodeNumber: currentEpisode.episodeNumber,
        episodeTitle: currentEpisode.title,
      };
    if (currentChannel) return { contentId: currentChannel.id };
    return {};
  }, [currentMovie, currentSeries, currentEpisode, currentChannel]);

  const kind: 'live' | 'movie' | 'series' = currentChannel
    ? 'live'
    : currentSeries && currentEpisode
    ? 'series'
    : currentMovie
    ? 'movie'
    : streamType;

  // V9.0.2: servidor = DNS da lista do item que está sendo enviado (perfil → conta → lista → DNS)
  const castPlaylistId =
    (currentChannel as any)?.playlistId || (currentEpisode as any)?.playlistId || (currentSeries as any)?.playlistId || (currentMovie as any)?.playlistId || null;
  const primaryDns = useMemo(() => getStreamServer(castPlaylistId), [getStreamServer, castPlaylistId]);
  const castAccount = useMemo(() => getStreamAccount(castPlaylistId), [getStreamAccount, castPlaylistId]);
  // V9.0.6: DNS + conta atual da lista em todo endereço mandado para a TV
  const listHost = useCallback((u: string) => withListAccount(withServerHost(u, primaryDns), castAccount), [primaryDns, castAccount]);

  // ── Endereços para a TV ────────────────────────────────────────────────────────────────────────
  // Ao vivo: o MESMO caminho do celular (servidor ativo do Gerenciador + proxy da Oracle, com o id do
  // aparelho). Antes ia o endereço cru do catálogo (sem trocar o servidor) e a TV ainda embrulhava de novo
  // no proxy — por isso o canal ao vivo não abria na TV.
  // Para a lista caber (canais × qualidades), cada canal vai só com o endereço no servidor ativo (`u`) e a
  // TV monta o do proxy pelo modelo `proxy` ({u} = endereço codificado): principal em .m3u8 (hls.js),
  // reserva no formato original (.ts → mpegts.js).
  const proxyTemplate = useMemo(() => {
    const probe = 'http://opentv.probe/x';
    return getProxiedStreamUrl(probe, undefined, undefined, deviceId).replace(encodeURIComponent(probe), '{u}');
  }, [deviceId]);

  const prefersTs = useCallback(
    (channelId?: string) => {
      const prof = channelId ? getChannelStreamProfile(channelId) : undefined;
      return prof?.preferred_format === 'ts' || prof?.preferred_player === 'mpegts';
    },
    [getChannelStreamProfile]
  );

  // Endereços prontos de um canal (usado para o canal atual nos campos antigos)
  const liveUrlsFor = useCallback(
    (rawUrl: string, channelId?: string): { url: string; fallback: string } => {
      const raw = listHost(rawUrl);
      const url = getProxiedStreamUrl(prefersTs(channelId) ? raw : convertToHlsUrl(raw), undefined, undefined, deviceId);
      const fb = getProxiedStreamUrl(raw, undefined, undefined, deviceId);
      return { url, fallback: fb !== url ? fb : '' };
    },
    [listHost, prefersTs, deviceId]
  );

  // Qualidades de cada canal: mesmo agrupamento do player ao vivo (nome sem "HD/FHD/4K")
  const qualityGroups = useMemo(() => {
    const map = new Map<string, Map<string, ChannelQualityVariant>>();
    if (!currentChannel) return map;
    for (const c of channels || []) {
      if (c.hidden) continue;
      const key = extractChannelQualityInfo(c.name).baseName.toLowerCase().replace(/[^a-z0-9]/g, '');
      if (!key) continue;
      let g = map.get(key);
      if (!g) {
        g = new Map();
        map.set(key, g);
      }
      if (c.qualities && c.qualities.length > 0) {
        for (const q of c.qualities) if (!g.has(q.resolution)) g.set(q.resolution, q);
      } else {
        const { detectedResolution } = extractChannelQualityInfo(c.name, c.groupTitle);
        const res = detectedResolution || c.resolution || 'SD';
        if (!g.has(res)) g.set(res, { resolution: res, streamUrl: c.streamUrl, originalName: c.name } as ChannelQualityVariant);
      }
    }
    return map;
  }, [channels, currentChannel]);

  const liveItemFor = useCallback(
    (ch: Channel): TvItem => {
      const item: TvItem = {
        id: ch.id,
        title: ch.name,
        num: ch.num,
        logo: ch.logo || '',
        u: listHost(ch.streamUrl),
      };
      if (prefersTs(ch.id)) item.ts = 1;
      const sid = extractStreamIdFromChannel(ch);
      if (sid) item.sid = sid;
      let variants: ChannelQualityVariant[] = ch.qualities && ch.qualities.length > 1 ? ch.qualities : [];
      if (variants.length < 2) {
        const key = extractChannelQualityInfo(ch.name).baseName.toLowerCase().replace(/[^a-z0-9]/g, '');
        const g = qualityGroups.get(key);
        variants = g ? Array.from(g.values()) : [];
      }
      if (variants.length > 1) {
        item.q = variants
          .slice()
          .sort((a, b) => (RESOLUTION_HIERARCHY[a.resolution] || 0) - (RESOLUTION_HIERARCHY[b.resolution] || 0))
          .map((v) => ({ r: v.resolution, u: listHost(v.streamUrl) }));
        item.cq = ch.currentQuality || ch.resolution || extractChannelQualityInfo(ch.name, ch.groupTitle).detectedResolution || '';
      }
      return item;
    },
    [listHost, prefersTs, qualityGroups]
  );

  // Filme/episódio: DIRETO no servidor ativo (a página /tv é HTTP — não gasta a banda da Oracle);
  // reserva pelo proxy da Oracle.
  const vodUrlsFor = useCallback(
    (rawUrl: string): { url: string; fallback: string } => {
      if (!rawUrl) return { url: '', fallback: '' };
      const direct = listHost(rawUrl);
      // V9.1.6: direto na TV; reserva pela Oracle (o provedor recusa alguns navegadores desde 30/10)
      const oracle = getProxiedStreamUrl(direct);
      return { url: direct, fallback: oracle !== direct ? oracle : '' };
    },
    [listHost]
  );

  // Canais da categoria atual (mesma regra do "trocar canal" do player)
  const liveChannelsPool = useMemo((): Channel[] => {
    if (!currentChannel) return [];
    const visible = (channels || []).filter((c) => !c.hidden);
    const renames: Record<string, string> = (categoryConfig as any)?.live?.renames || {};
    let pool: Channel[] = [];
    if (activeChannelCategory === 'Favoritos') {
      pool = visible.filter((c) => isFavorite(c.id));
    } else if (activeChannelCategory && activeChannelCategory !== 'Todos') {
      pool = visible.filter(
        (c) => c.groupTitle === activeChannelCategory || renames[c.groupTitle] === activeChannelCategory
      );
    }
    if (pool.length === 0) pool = visible;
    if (!pool.some((c) => c.id === currentChannel.id)) pool = [currentChannel, ...pool];
    return pool;
  }, [currentChannel, channels, categoryConfig, activeChannelCategory, isFavorite]);

  // Episódios da série em ordem (temporada, episódio) — mesma regra do player
  const sortEpisodes = useCallback(
    (rawList: Episode[]): Episode[] => {
      if (!currentEpisode) return [];
      const valid = (rawList || []).filter(
        (ep) => ep && ep.streamUrl && !ep.id?.includes('-dummy') && ep.title !== 'Carregar Episódios'
      );
      if (!valid.some((ep) => ep.id === currentEpisode.id)) valid.push(currentEpisode);
      return [...valid].sort((a, b) => {
        const sA = parseNum(a.seasonNumber || (a as any).season, 1);
        const sB = parseNum(b.seasonNumber || (b as any).season, 1);
        if (sA !== sB) return sA - sB;
        const eA = parseNum(a.episodeNumber || (a as any).episode || (a as any).num, 0);
        const eB = parseNum(b.episodeNumber || (b as any).episode || (b as any).num, 0);
        return eA - eB;
      });
    },
    [currentEpisode]
  );
  const sortedEpisodes = useMemo((): Episode[] => {
    if (!currentSeries || !currentEpisode) return [];
    const contextSeries = series?.find((s) => s.id === currentSeries.id);
    const a = currentSeries.episodes || [];
    const b = contextSeries?.episodes || [];
    return sortEpisodes(b.length > a.length ? b : a);
  }, [currentSeries, currentEpisode, series, sortEpisodes]);

  /**
   * V8.8.8: garante a lista COMPLETA de episódios antes de mandar a série para a TV (senão o controle e a
   * TV ficavam só com o episódio atual). Devolve undefined quando a lista já está completa.
   */
  const ensureFullEpisodes = useCallback(async (): Promise<Episode[] | undefined> => {
    if (!currentSeries || !currentEpisode) return undefined;
    if (sortedEpisodes.length > 1) return undefined;
    try {
      const full = await loadSeriesEpisodes(currentSeries.id);
      if (full && Array.isArray(full.episodes) && full.episodes.length > 1) return sortEpisodes(full.episodes);
    } catch {}
    return undefined;
  }, [currentSeries, currentEpisode, sortedEpisodes, loadSeriesEpisodes, sortEpisodes]);

  const effectiveTitle = streamTitle || channelName || 'Transmissão OPEN TV';

  /** Monta o pacote completo do "tocar" (lista + posição atual). */
  const buildPlayPayload = useCallback(
    (startAt: number, episodesOverride?: Episode[]): Record<string, any> | null => {
      let items: TvItem[] = [];
      const epList = episodesOverride && episodesOverride.length > 0 ? episodesOverride : sortedEpisodes;
      let index = 0;

      if (kind === 'live' && currentChannel) {
        let pool = liveChannelsPool;
        let curIdx = pool.findIndex((c) => c.id === currentChannel.id);
        if (pool.length > MAX_LIVE_ITEMS) {
          const half = Math.floor(MAX_LIVE_ITEMS / 2);
          const start = Math.max(0, Math.min(curIdx - half, pool.length - MAX_LIVE_ITEMS));
          pool = pool.slice(start, start + MAX_LIVE_ITEMS);
          curIdx = curIdx - start;
        }
        items = pool.map(liveItemFor);
        index = Math.max(0, curIdx);
      } else if (kind === 'series' && currentSeries && currentEpisode) {
        items = epList.map((ep) => {
          const s = parseNum(ep.seasonNumber || (ep as any).season, 1);
          const e = parseNum(ep.episodeNumber || (ep as any).episode || (ep as any).num, 1);
          const urls = vodUrlsFor(ep.streamUrl);
          return {
            id: ep.id,
            title: `T${s}:E${e}${ep.title ? ` • ${ep.title}` : ''}`,
            s,
            e,
            epTitle: ep.title || '',
            url: urls.url,
            fallback: urls.fallback,
            // Assistido (✓) e quanto já viu (%) — a TV mostra na lista de episódios
            ...(isContentWatched(ep.id) ? { w: 1 as const } : {}),
            ...(() => {
              const pr = getContentProgress(ep.id);
              return pr && !pr.isCompleted && pr.percentage > 0 ? { p: pr.percentage } : {};
            })(),
          };
        });
        index = Math.max(0, epList.findIndex((ep) => ep.id === currentEpisode.id));
        // O episódio atual vai com o endereço que o celular está tocando (se for direto e funcionou)
        if (items[index] && proxiedStreamUrl && !isOracleUrl(proxiedStreamUrl)) {
          items[index] = { ...items[index], url: proxiedStreamUrl };
        }
      } else if (currentMovie) {
        const urls = vodUrlsFor(currentMovie.streamUrl || directStreamUrl || streamUrl || '');
        const url = proxiedStreamUrl && !isOracleUrl(proxiedStreamUrl) ? proxiedStreamUrl : urls.url;
        items = [{ id: currentMovie.id, title: currentMovie.title, url, fallback: urls.fallback }];
      } else {
        // Sem conteúdo no contexto: manda o que veio do player
        const raw = directStreamUrl || streamUrl || proxiedStreamUrl || '';
        if (!raw) return null;
        items = [{ id: 'atual', title: effectiveTitle, url: proxiedStreamUrl || raw, fallback: '' }];
      }

      const cur = items[index];
      if (!cur || !(cur.url || cur.u)) return null;
      // Campos antigos (página da TV antiga em cache): endereço pronto do item atual
      const legacy =
        kind === 'live' && currentChannel
          ? liveUrlsFor(currentChannel.streamUrl, currentChannel.id)
          : { url: cur.url || '', fallback: cur.fallback || '' };

      return {
        v: 2,
        kind,
        title: kind === 'series' && currentSeries ? currentSeries.title : cur.title,
        seriesTitle: currentSeries?.title || '',
        streamType: kind,
        // campos antigos (TV com página antiga em cache continua tocando)
        streamUrl: legacy.url,
        fallbackUrl: legacy.fallback || '',
        proxy: proxyTemplate,
        epgDns: primaryDns,
        currentTime: kind === 'live' ? 0 : startAt,
        items,
        index,
      };
    },
    [
      kind,
      currentChannel,
      currentSeries,
      currentEpisode,
      currentMovie,
      liveChannelsPool,
      liveItemFor,
      liveUrlsFor,
      proxyTemplate,
      primaryDns,
      sortedEpisodes,
      vodUrlsFor,
      isContentWatched,
      getContentProgress,
      proxiedStreamUrl,
      directStreamUrl,
      streamUrl,
      effectiveTitle,
    ]
  );

  /** Envia um comando pelo tv_sync.php (único caminho que a página /tv escuta). */
  const postCommand = useCallback(
    async (sessionId: string, action: string, payload: Record<string, any> = {}) => {
      const isPlay = action === 'play';
      const res = await fetch('/api/tv_sync.php?action=send_command', {
        method: 'POST',
        headers: tvSyncHeaders(),
        body: JSON.stringify({
          session_id: sessionId,
          command: { action, payload: isPlay ? { ...payload, ...contentFields } : payload },
          ...(isPlay ? ownerFields : {}),
        }),
      });
      let data: any = null;
      try {
        data = await res.json();
      } catch {}
      if (isPlay && data?.success) saveCastPayload(sessionId, payload);
      return data || { success: false };
    },
    [tvSyncHeaders, ownerFields, contentFields]
  );

  return { buildPlayPayload, ensureFullEpisodes, postCommand, tvSyncHeaders, ownerFields, contentFields, kind, effectiveTitle };
}

export const CastModal: React.FC<CastModalProps> = ({
  isOpen,
  onClose,
  videoElement,
  streamTitle,
  directStreamUrl,
  proxiedStreamUrl,
  channelName,
  streamUrl,
  streamType = 'live',
}) => {
  const { setCastState, isCasting, castSessionId, castDeviceName, castTvCode, stopPlayback } = useIPTV();
  const { buildPlayPayload, ensureFullEpisodes, postCommand, tvSyncHeaders, ownerFields, contentFields, kind, effectiveTitle } = useCastSender({
    streamTitle,
    directStreamUrl,
    proxiedStreamUrl,
    channelName,
    streamUrl,
    streamType,
  });

  const [tvCodeInput, setTvCodeInput] = useState('');
  const [isPairing, setIsPairing] = useState(false);
  const [pairError, setPairError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [isTvOnline, setIsTvOnline] = useState(false);
  const [tvPlaybackState, setTvPlaybackState] = useState<any>(null);
  const [isSendingCommand, setIsSendingCommand] = useState(false);
  const [tvVolume, setTvVolume] = useState<number>(1);
  const [isTvMuted, setIsTvMuted] = useState(false);

  // Lista de TVs conhecidas
  const [savedTvs, setSavedTvs] = useState<PairedTvData[]>(() => {
    if (typeof window !== 'undefined') {
      try {
        const raw = localStorage.getItem('opentv_saved_tvs');
        if (raw) return JSON.parse(raw);
        const single =
          localStorage.getItem('opentv_connection_session_id') ||
          localStorage.getItem('nexus_paired_tv_session');
        if (single) return [JSON.parse(single)];
      } catch {}
    }
    return [];
  });
  const [showCodeInput, setShowCodeInput] = useState(false);

  // command_id do último comando enviado (a TV confirma pelo get_tv_status)
  const lastCommandIdRef = useRef<string | null>(null);
  const [commandAcked, setCommandAcked] = useState(false);

  // TV ativa no momento (sem persistência — user escolhe da lista salva)
  // Mensagens de situação fecham sozinhas
  useEffect(() => {
    if (!statusMessage) return;
    const t = setTimeout(() => setStatusMessage(null), 4000);
    return () => clearTimeout(t);
  }, [statusMessage]);

  // Ao reabrir durante o Cast, já mostra a TV que está transmitindo (vem do app, não se perde ao fechar)
  const [pairedTv, setPairedTv] = useState<PairedTvData | null>(() =>
    isCasting && castSessionId
      ? { sessionId: castSessionId, tvName: castDeviceName || 'Smart TV', tvCode: castTvCode || '' }
      : null
  );

  const isIOS = useMemo(() => {
    if (typeof navigator === 'undefined') return false;
    return (
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    );
  }, []);

  const isAndroid = useMemo(() => {
    if (typeof navigator === 'undefined') return false;
    return /Android/i.test(navigator.userAgent);
  }, []);

  const saveToTvList = useCallback((tv: PairedTvData) => {
    const updated = { ...tv, lastUsed: Date.now() };
    setSavedTvs((prev) => {
      const filtered = prev.filter((t) => t.sessionId !== tv.sessionId);
      const newList = [updated, ...filtered].slice(0, 5);
      try {
        localStorage.setItem('opentv_saved_tvs', JSON.stringify(newList));
      } catch {}
      return newList;
    });
  }, []);

  const removeFromTvList = useCallback((sessionId: string) => {
    setSavedTvs((prev) => {
      const newList = prev.filter((t) => t.sessionId !== sessionId);
      try {
        localStorage.setItem('opentv_saved_tvs', JSON.stringify(newList));
      } catch {}
      return newList;
    });
  }, []);

  const timeAgo = (ts?: number): string => {
    if (!ts) return '';
    const diff = Date.now() - ts;
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'agora mesmo';
    if (m < 60) return `há ${m}min`;
    const h = Math.floor(m / 60);
    if (h < 24) return `há ${h}h`;
    return `há ${Math.floor(h / 24)}d`;
  };

  // Código no endereço (ex: #pair=123456 ao escanear o QR Code da TV)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const match = window.location.hash.match(/pair=(\d{6})/i);
      if (match && match[1]) setTvCodeInput(match[1]);
    }
  }, [isOpen]);

  // Situação da TV pareada: pergunta ao tv_sync.php a cada 2 s
  useEffect(() => {
    if (!isOpen || !pairedTv) return;
    let alive = true;
    const check = async () => {
      try {
        const res = await fetch(
          `/api/tv_sync.php?action=get_tv_status&session_id=${encodeURIComponent(pairedTv.sessionId)}`
        );
        const data = await res.json();
        if (!alive || !data?.success) return;
        setIsTvOnline(!!data.is_online);
        setTvPlaybackState(data.current_playback || null);
        if (
          data.last_ack_command_id &&
          lastCommandIdRef.current &&
          data.last_ack_command_id === lastCommandIdRef.current
        ) {
          setCommandAcked(true);
        }
        const pb = data.current_playback;
        if (pb && pb.contentId && pb.streamType !== 'live' && Number(pb.currentTime) > 0) {
          lastTvPosition = { contentId: String(pb.contentId), time: Number(pb.currentTime) };
        }
        if (pb) {
          if (typeof pb.volume === 'number') setTvVolume(pb.volume);
          if (typeof pb.isMuted === 'boolean') setIsTvMuted(pb.isMuted);
        }
      } catch {
        if (alive) setIsTvOnline(false);
      }
    };
    check();
    const timer = setInterval(check, 2000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [isOpen, pairedTv]);

  // Posição para começar na TV: a do vídeo do celular; durante o Cast (o celular soltou o vídeo) a da TV.
  const currentStartAt = useCallback((): number => {
    const local = videoElement?.currentTime || 0;
    if (local > 0) return local;
    const pb = tvPlaybackState;
    if (isCasting && pb && pb.contentId && pb.contentId === (contentFields as any).contentId) {
      return Number(pb.currentTime) || 0;
    }
    return 0;
  }, [videoElement, tvPlaybackState, isCasting, contentFields]);

  /** Manda o conteúdo atual para a TV. */
  const playOnTv = useCallback(
    async (tv: PairedTvData, okMessage: string) => {
      const fullEps = await ensureFullEpisodes();
      const payload = buildPlayPayload(currentStartAt(), fullEps);
      if (!payload) {
        setStatusMessage(`Conectado à ${tv.tvName}! Inicie um vídeo para transmitir.`);
        return;
      }
      setIsSendingCommand(true);
      setCommandAcked(false);
      try {
        const data = await postCommand(tv.sessionId, 'play', payload);
        if (data.error === 'screen_limit') {
          setStatusMessage(data.message || 'Limite de telas atingido.');
        } else if (data.success) {
          if (data.command_id) lastCommandIdRef.current = data.command_id;
          setStatusMessage(okMessage);
          setCastState(true, tv.tvName || 'Smart TV', tv.sessionId, tv.tvCode);
          if (videoElement && !videoElement.paused) videoElement.pause();
          // V8.8.7: transmitindo → fecha a janela E o player do celular (volta para a lista, para não tocar
          // outra coisa aqui) e abre o controle remoto (dá para minimizar)
          setTimeout(() => {
            onClose();
            stopPlayback();
            openCastRemote();
          }, 700);
        } else {
          setStatusMessage(`${tv.tvName} pode estar offline. Verifique se a página da TV está aberta.`);
        }
      } catch {
        setStatusMessage('Erro de conexão ao enviar para a TV.');
      } finally {
        setIsSendingCommand(false);
      }
    },
    [buildPlayPayload, ensureFullEpisodes, currentStartAt, postCommand, setCastState, videoElement, onClose, stopPlayback]
  );

  // Comandos do controle (pausar, avançar, trocar canal/episódio, volume, parar)
  const sendTvCommand = useCallback(
    async (action: string, payload: any = {}) => {
      if (!pairedTv) return;
      try {
        setIsSendingCommand(true);
        setCommandAcked(false);
        const data = await postCommand(pairedTv.sessionId, action, payload);
        if (data?.success) {
          if (data.command_id) lastCommandIdRef.current = data.command_id;
          if (action === 'stop') setCastState(false);
        } else {
          setStatusMessage('Não foi possível enviar o comando para a TV.');
        }
      } catch {
        setStatusMessage('Erro de conexão ao enviar comando para a TV.');
      } finally {
        setIsSendingCommand(false);
      }
    },
    [pairedTv, postCommand, setCastState]
  );

  // Parear novo código (manual ou pelo QR Code)
  const handlePairTv = async (e?: React.FormEvent, customCode?: string) => {
    if (e) e.preventDefault();
    const cleanCode = (customCode || tvCodeInput).replace(/\D/g, '');
    if (cleanCode.length !== 6) {
      setPairError('Digite exatamente os 6 dígitos que aparecem na TV.');
      return;
    }

    // Retentativa só para falha de REDE (não para código errado)
    const maxAttempts = 3;
    let lastNetworkError: unknown = null;

    try {
      setIsPairing(true);
      setPairError(null);
      const clientId = 'client_' + Math.random().toString(36).substring(2, 9);

      let data: any = null;
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          const res = await fetch('/api/tv_sync.php?action=pair_code', {
            method: 'POST',
            headers: tvSyncHeaders(),
            body: JSON.stringify({
              tv_code: cleanCode,
              client_id: clientId,
              device_name: isIOS ? 'iPhone' : isAndroid ? 'Android' : 'Dispositivo Web',
              ...ownerFields,
            }),
          });
          data = await res.json();
          lastNetworkError = null;
          break;
        } catch (networkErr) {
          lastNetworkError = networkErr;
          if (attempt < maxAttempts) {
            setStatusMessage(`Instabilidade de rede, tentando de novo (${attempt}/${maxAttempts})...`);
            await new Promise((resolve) => setTimeout(resolve, 800 * attempt));
          }
        }
      }

      if (lastNetworkError) {
        setPairError('Erro de conexão com o servidor ao vincular código. Verifique sua internet e tente novamente.');
        return;
      }

      if (data?.success) {
        const newTv: PairedTvData = {
          sessionId: data.session_id,
          tvCode: data.tv_code,
          tvName: data.tv_name || 'Smart TV',
          pairedAt: Date.now(),
          lastUsed: Date.now(),
        };
        setPairedTv(newTv);
        saveToTvList(newTv);
        setShowCodeInput(false);
        setIsTvOnline(true);
        lastCommandIdRef.current = null;
        await playOnTv(newTv, `Conectado à ${newTv.tvName}! Transmitindo...`);
      } else {
        setPairError(data?.error || 'Código incorreto ou TV desconectada.');
      }
    } catch {
      setPairError('Erro de conexão com o servidor ao vincular código.');
    } finally {
      setIsPairing(false);
    }
  };

  // Desconectar a TV ativa (continua na lista salva)
  const handleUnpairTv = async () => {
    if (pairedTv) {
      try {
        await fetch('/api/tv_sync.php?action=unpair', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_id: pairedTv.sessionId }),
        });
      } catch {}
      saveToTvList({ ...pairedTv, lastUsed: Date.now() });
    }
    setPairedTv(null);
    setTvPlaybackState(null);
    setCommandAcked(false);
    lastCommandIdRef.current = null;
    setCastState(false);
    setStatusMessage('TV desconectada. Reconecte a qualquer momento pela lista.');
  };

  // Reconectar a uma TV já conhecida
  const handleConnectSavedTv = useCallback(
    async (tv: PairedTvData) => {
      setPairedTv(tv);
      setIsTvOnline(true);
      setCommandAcked(false);
      lastCommandIdRef.current = null;
      saveToTvList({ ...tv, lastUsed: Date.now() });
      await playOnTv(tv, `Reconectado e transmitindo na ${tv.tvName}!`);
    },
    [saveToTvList, playOnTv]
  );

  const handleForgetTv = useCallback(
    (sessionId: string) => {
      removeFromTvList(sessionId);
      if (pairedTv?.sessionId === sessionId) {
        setPairedTv(null);
        setTvPlaybackState(null);
      }
    },
    [pairedTv, removeFromTvList]
  );

  const handleCastCurrentStream = () => {
    if (pairedTv) playOnTv(pairedTv, `Transmitindo na ${pairedTv.tvName || 'TV'}!`);
  };

  const handleTvVolumeChange = (step: number) => {
    const next = Math.max(0, Math.min(1, Math.round((tvVolume + step) * 10) / 10));
    setTvVolume(next);
    setIsTvMuted(false);
    sendTvCommand('set_volume', { volume: next });
  };

  const handleTvToggleMute = () => {
    setIsTvMuted(!isTvMuted);
    sendTvCommand('toggle_mute');
  };

  // Transmissão nativa: AirPlay (iPhone/iPad)
  const handleNativeCast = async () => {
    setStatusMessage(null);
    if (!videoElement) {
      setStatusMessage('Inicie um vídeo antes de transmitir.');
      return;
    }
    const video = videoElement as any;
    // Canais tocados pelo hls.js (fonte "blob:") não vão por AirPlay — a Apple só transmite o vídeo nativo dela.
    if (typeof video.currentSrc === 'string' && video.currentSrc.startsWith('blob:')) {
      setStatusMessage('Este canal não pode ser enviado por AirPlay. Use o código da TV acima.');
      return;
    }
    if (typeof video.webkitShowPlaybackTargetPicker === 'function') {
      try {
        video.webkitShowPlaybackTargetPicker();
        setStatusMessage('Selecione sua Smart TV na janela do AirPlay.');
        const onAirPlayChange = () => {
          if (video.webkitCurrentPlaybackTargetIsWireless) setCastState(true, 'Apple AirPlay');
          else setCastState(false);
        };
        video.removeEventListener('webkitcurrentplaybacktargetiswirelesschanged', onAirPlayChange);
        video.addEventListener('webkitcurrentplaybacktargetiswirelesschanged', onAirPlayChange);
        return;
      } catch (err) {
        console.warn('AirPlay target picker error:', err);
      }
    }
    setStatusMessage('Para esta TV, use o OPEN TV CONNECTION digitando o código de 6 dígitos da TV acima.');
  };

  if (!isOpen) return null;

  // O que a TV está tocando agora (ela pode ter trocado de canal/episódio sozinha)
  const tvKind: string = tvPlaybackState?.streamType || kind;
  const tvIsLive = tvKind === 'live';
  const tvIsSeries = tvKind === 'series';
  const tvNowTitle: string = tvPlaybackState?.title
    ? `${tvPlaybackState.title}${tvPlaybackState.subtitle ? ` — ${tvPlaybackState.subtitle}` : ''}`
    : '';
  const hasPrev = tvPlaybackState ? tvPlaybackState.hasPrev !== false : true;
  const hasNext = tvPlaybackState ? tvPlaybackState.hasNext !== false : true;
  const tvPaused = tvPlaybackState?.isPlaying === false;

  // Padrão de botões do sistema (mesmo do player e do menu)
  const btn =
    'h-11 px-3 rounded-xl bg-zinc-800/90 hover:bg-zinc-700 active:scale-95 text-zinc-200 hover:text-white text-xs font-bold transition cursor-pointer flex items-center justify-center gap-1.5 disabled:opacity-35 disabled:cursor-not-allowed disabled:active:scale-100 focus:outline-none focus:ring-2 focus:ring-theme-accent';
  const btnPrimary =
    'h-11 px-4 rounded-xl bg-theme-accent hover:opacity-90 active:scale-95 text-black text-xs font-black shadow-md shadow-theme-accent-sm transition cursor-pointer flex items-center justify-center gap-1.5 disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-white/60';
  const btnStop =
    'h-11 px-3 rounded-xl bg-zinc-800/90 hover:bg-red-950/60 active:scale-95 text-red-400 hover:text-red-300 text-xs font-bold transition cursor-pointer flex items-center justify-center gap-1.5 focus:outline-none focus:ring-2 focus:ring-red-400/60';
  const card = 'p-3.5 sm:p-4 rounded-2xl bg-zinc-900/80 border border-zinc-800 space-y-3';
  const sectionLabel = 'text-[11px] font-bold uppercase tracking-wider text-zinc-400';

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-md p-2.5 sm:p-4 animate-in fade-in select-none"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-zinc-950 border border-zinc-800 rounded-2xl sm:rounded-3xl max-w-lg w-full shadow-2xl overflow-hidden flex flex-col max-h-[92dvh]"
      >
        {/* Cabeçalho: logo do OPEN TV (igual ao menu) + o que vai para a TV */}
        <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-zinc-800/80 flex items-center justify-between bg-zinc-950/95 gap-3 shrink-0">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-10 h-10 rounded-xl bg-theme-accent flex items-center justify-center text-black shadow-md shadow-theme-accent-sm shrink-0">
              <Tv className="w-5 h-5 stroke-[2.5]" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="font-black text-sm sm:text-base text-white tracking-wider">OPEN TV</span>
                <span className="text-[11px] font-bold text-theme-accent-light uppercase tracking-wider">Transmitir</span>
              </div>
              <p className="text-[11px] sm:text-xs text-zinc-400 truncate">{effectiveTitle}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-xl bg-zinc-800/90 hover:bg-zinc-700 text-zinc-300 hover:text-white transition cursor-pointer flex items-center justify-center shrink-0"
            title="Fechar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {statusMessage && (
          <div className="mx-3 sm:mx-4 mt-3 p-2.5 sm:p-3 rounded-xl bg-zinc-900 border border-zinc-800 text-xs text-zinc-200 flex items-start justify-between gap-2">
            <div className="flex items-start gap-2 min-w-0 flex-1">
              <Info className="w-4 h-4 text-theme-accent shrink-0 mt-0.5" />
              <span className="leading-relaxed text-[11px] sm:text-xs break-words">{statusMessage}</span>
            </div>
            <button onClick={() => setStatusMessage(null)} className="text-zinc-500 hover:text-white shrink-0 font-bold px-1">
              ✕
            </button>
          </div>
        )}

        <div className="p-3 sm:p-4 space-y-3 overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-zinc-800">
          {/* CÓDIGO DA TV */}
          <div className={card}>
            <div className="flex items-center justify-between gap-2">
              <span className={sectionLabel}>Código da TV</span>
              {pairedTv && (
                <button
                  onClick={handleUnpairTv}
                  className="text-[11px] font-bold text-zinc-400 hover:text-theme-accent-light transition cursor-pointer"
                >
                  Trocar TV
                </button>
              )}
            </div>

            {pairedTv ? (
              <div className="space-y-3">
                {/* TV conectada */}
                <div className="flex items-center gap-3 p-2.5 rounded-xl bg-zinc-950 border border-zinc-800 min-w-0">
                  <div className="w-9 h-9 rounded-xl bg-zinc-800 text-theme-accent flex items-center justify-center shrink-0">
                    <Tv className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold text-white truncate flex items-center gap-1.5">
                      <span className="truncate">{pairedTv.tvName}</span>
                      <span className={`w-2 h-2 rounded-full shrink-0 ${isTvOnline ? 'bg-emerald-400' : 'bg-zinc-600'}`} />
                    </p>
                    <p className="text-[11px] text-zinc-500 truncate">
                      {tvNowTitle ? `Na TV: ${tvNowTitle}` : isTvOnline ? 'Online' : 'Aguardando a TV'}
                    </p>
                  </div>
                  {commandAcked ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                  ) : isSendingCommand ? (
                    <Loader2 className="w-4 h-4 text-zinc-400 animate-spin shrink-0" />
                  ) : null}
                </div>

                <button onClick={handleCastCurrentStream} disabled={isSendingCommand} className={`${btnPrimary} w-full`}>
                  {isSendingCommand ? <Loader2 className="w-4 h-4 animate-spin" /> : <Cast className="w-4 h-4" />}
                  <span className="truncate">Transmitir agora na {pairedTv.tvName}</span>
                </button>

                {/* V8.8.7: o controle completo fica no "Controle" (barra Transmitindo), que abre e minimiza */}
                {isCasting && castSessionId === pairedTv.sessionId && (
                  <button
                    onClick={() => {
                      onClose();
                      openCastRemote();
                    }}
                    className={`${btn} w-full`}
                  >
                    <Tv className="w-4 h-4" />
                    Abrir controle remoto
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-2.5">
                {savedTvs.length > 0 && !showCodeInput && (
                  <div className="space-y-2">
                    {savedTvs.map((tv) => (
                      <div key={tv.sessionId} className="flex items-center gap-2">
                        <button
                          onClick={() => handleConnectSavedTv(tv)}
                          className="flex-1 min-w-0 flex items-center gap-3 p-2.5 rounded-xl bg-zinc-950 border border-zinc-800 hover:border-theme-accent transition cursor-pointer text-left focus:outline-none focus:ring-2 focus:ring-theme-accent"
                        >
                          <div className="w-9 h-9 rounded-xl bg-zinc-800 text-theme-accent flex items-center justify-center shrink-0">
                            <Tv className="w-4 h-4" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-bold text-white truncate">{tv.tvName}</p>
                            <p className="text-[11px] text-zinc-500 truncate">
                              {tv.tvCode.slice(0, 3)} {tv.tvCode.slice(3)}
                              {tv.lastUsed && ` • ${timeAgo(tv.lastUsed)}`}
                            </p>
                          </div>
                          <span className="text-[11px] font-black text-theme-accent shrink-0">Conectar</span>
                        </button>
                        <button
                          onClick={() => handleForgetTv(tv.sessionId)}
                          className="w-9 h-9 rounded-xl bg-zinc-800/90 hover:bg-zinc-700 text-zinc-500 hover:text-red-400 transition cursor-pointer flex items-center justify-center shrink-0"
                          title="Esquecer esta TV"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => {
                        setShowCodeInput(true);
                        setPairError(null);
                        setTvCodeInput('');
                      }}
                      className={`${btn} w-full`}
                    >
                      <Cast className="w-4 h-4" />
                      Conectar outra TV
                    </button>
                  </div>
                )}

                {(showCodeInput || savedTvs.length === 0) && (
                  <div className="space-y-2">
                    {savedTvs.length > 0 && showCodeInput && (
                      <button
                        onClick={() => setShowCodeInput(false)}
                        className="text-[11px] font-bold text-zinc-400 hover:text-theme-accent-light transition cursor-pointer"
                      >
                        ← TVs conectadas
                      </button>
                    )}
                    <p className="text-xs text-zinc-300 leading-relaxed">
                      1. No navegador da TV, abra <strong className="text-theme-accent-light">iptv.mariozeli.com.br/tv</strong>
                    </p>
                    <p className="text-xs text-zinc-300 leading-relaxed">2. Digite o código de 6 números que aparece na TV:</p>
                    <form onSubmit={handlePairTv} className="space-y-2 pt-1">
                      <div className="flex gap-2 min-w-0">
                        <input
                          type="text"
                          inputMode="numeric"
                          maxLength={6}
                          value={tvCodeInput}
                          onChange={(e) => setTvCodeInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          placeholder="000000"
                          className="flex-1 min-w-0 h-12 px-3 rounded-xl bg-zinc-950 border border-zinc-700 text-center font-mono text-xl font-black tracking-[0.35em] text-white placeholder:text-zinc-700 focus:outline-none focus:border-theme-accent focus:ring-2 focus:ring-theme-accent transition"
                        />
                        <button
                          type="submit"
                          disabled={isPairing || tvCodeInput.replace(/\D/g, '').length !== 6}
                          className={`${btnPrimary} h-12 shrink-0`}
                        >
                          {isPairing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Cast className="w-4 h-4" />}
                          <span>Conectar</span>
                        </button>
                      </div>
                      {pairError && (
                        <div className="p-2.5 rounded-xl bg-red-950/50 border border-red-500/40 text-[11px] text-red-200 flex items-center gap-1.5">
                          <AlertCircle className="w-3.5 h-3.5 text-red-400 shrink-0" />
                          <span>{pairError}</span>
                        </div>
                      )}
                    </form>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* TRANSMISSÃO NATIVA — AirPlay no iPhone/iPad; no Android, espelhamento de tela do aparelho */}
          {(isIOS || isAndroid) && (
            <div className={card}>
              <span className={sectionLabel}>Transmissão nativa</span>
              {isIOS ? (
                <button onClick={handleNativeCast} className={`${btn} w-full justify-start h-auto py-3`}>
                  <Airplay className="w-4 h-4 text-theme-accent shrink-0" />
                  <span className="flex-1 text-left">
                    Apple AirPlay
                    <span className="block text-[11px] font-normal text-zinc-500">Apple TV e TVs com AirPlay (Samsung, LG, TCL)</span>
                  </span>
                </button>
              ) : (
                <p className="text-xs text-zinc-400 leading-relaxed">
                  Para TVs que aceitam espelhamento: deslize o menu rápido do Android e toque em{' '}
                  <strong className="text-zinc-200">Smart View</strong> ou <strong className="text-zinc-200">Transmitir tela</strong>.
                  Se a TV não aparecer, use o código da TV acima.
                </p>
              )}
            </div>
          )}
        </div>

        <div className="px-4 sm:px-5 py-3 border-t border-zinc-800/80 bg-zinc-950/95 flex items-center justify-between gap-2 shrink-0">
          <a
            href="/tv/"
            target="_blank"
            rel="noreferrer"
            className="text-[11px] font-bold text-zinc-400 hover:text-theme-accent-light flex items-center gap-1 truncate"
          >
            <span className="truncate">Abrir a página da TV</span>
            <ExternalLink className="w-3 h-3 shrink-0" />
          </a>
          <button onClick={onClose} className={`${btn} h-9 px-4`}>
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};
